import { DecisionContext, DbotEventDispatcher } from "@/dbot/common/commonDomain";
import { makeAutoObservable } from "mobx";
import { newUuid } from "@/utils/uuid";

import { TMessage } from "@/i18n/tt";
import { SelectableWithId } from "@/common/common";
import { DecisionType } from "@/common/decisions";
import { SelectableWithIdModel, StringFieldModel } from "@/common/commonForms";

export type ElectNominationCommittee = {
  type: DecisionType.electNominationCommittee;
  newNominationCommitteeMembersByName: Array<SelectableWithId>;
};

export class ElectNominationCommitteeForm {
  readonly type = DecisionType.electNominationCommittee;
  readonly dispatch: DbotEventDispatcher;
  newNominationCommitteeMembersByName: SelectableWithIdModel;
  isModalDialogVisible: boolean;
  committeeName: StringFieldModel;
  electionOfMembersWarning?: TMessage;

  constructor(
    dispatch: DbotEventDispatcher,
    private context: DecisionContext<ElectNominationCommittee>,
    public data?: ElectNominationCommittee,
  ) {
    this.newNominationCommitteeMembersByName = new SelectableWithIdModel({
      value: data?.newNominationCommitteeMembersByName,
      required: true,
    });
    this.isModalDialogVisible = false;
    this.committeeName = new StringFieldModel({ required: true });
    this.dispatch = dispatch;
    makeAutoObservable(this);
  }

  displayAddNewCommitteeDialog() {
    this.isModalDialogVisible = true;
    this.committeeName.clear();
  }

  hideAddNewCommitteeDialog() {
    this.isModalDialogVisible = false;
  }

  toggleCommittee(id: string) {
    if (this.newNominationCommitteeMembersByName.isSelected(id)) {
      this.newNominationCommitteeMembersByName.toggle(id);
    } else {
      this.newNominationCommitteeMembersByName.select(id);
    }
  }

  filterSelected() {
    return this.newNominationCommitteeMembersByName.value.filter((e) => e.isSelected);
  }

  addNewCommitteeAndClose() {
    this.committeeName.validate();
    if (this.committeeName.isValid) {
      const item: SelectableWithId = {
        id: newUuid(),
        name: this.committeeName.inputValue,
        isSelected: true,
      };
      this.newNominationCommitteeMembersByName.add(item);
      this.committeeName.clear();
      this.isModalDialogVisible = false;
      return item;
    }
    return null;
  }

  removeCommittee(id: string) {
    const newNominationCommittee = this.newNominationCommitteeMembersByName.value.filter(
      (e) => e.id !== id,
    );
    this.newNominationCommitteeMembersByName.setValue(newNominationCommittee);
  }

  get isProjectLocked(): boolean {
    return this.context.isLocked;
  }

  get isValid() {
    return !this.electionOfMembersWarning;
  }

  validateNewNominationCommitteeMembers() {
    const noMembers = this.newNominationCommitteeMembersByName.value.length === 0;
    const noMembersSelected = this.newNominationCommitteeMembersByName.value.every(
      (e) => !e.isSelected,
    );

    if (noMembers) {
      this.electionOfMembersWarning = {
        key: "decisions.electNominationCommittee.add-member.warning",
      };
    } else if (noMembersSelected) {
      this.electionOfMembersWarning = {
        key: "decisions.electNominationCommittee.member.isSelected.warning",
      };
    }
    return this.newNominationCommitteeMembersByName.isValid;
  }

  validate(): boolean {
    this.electionOfMembersWarning = undefined;
    this.validateNewNominationCommitteeMembers();
    return this.isValid;
  }
}
